import React, { useEffect, useMemo, useState } from 'react';
import pt from 'prop-types';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { useIndex, usePrevious } from './hooks';
import {
  backgroundsLedBy, calculateAdditionalPoses, firstBuyAllImageType, poseSheetAttrs,
} from './functions';
import PreviewArea from './PreviewArea';
import styles from './MultiPose.module.scss';
import Carousel from './Carousel';
import NavBar from './NavBar';
import routes from '../../../routes';
import useImageOrientation from '../../../hooks/useImageOrientation';
import { IMAGE_ORIENTATION_CACHE } from '../../../utils/variables';
import {
  getAppropriateBg,
} from '../../_pages/_ElementaryMultiPose/MultiPosePackageBuilder/steps/FavoritePoseSelection';
import ProgressSheets from './ProgressSheets';
import useForegrounds from '../../../hooks/useForegrounds';
import usePricelist from '../../../hooks/usePricelist';
import usePrintTemplate from '../../../hooks/usePrintTemplate';
import { useCustomer } from '../../../contexts/CustomerContext';
import useStudentForegrounds from "../../../hooks/useStudentForegrounds";
import useQueryString from "../../../hooks/use-query-string";
import visiblePoses from './visiblePoses';
import eligiblePoses from './poseEligibility';

import useImageOrientations from '../../../hooks/useImageOrientations';

// Shown in place of the pose carousel when the product refuses every photo the
// customer has. Deliberately does not say "press Back": on a single-sheet edit
// Back is disabled, and advice that cannot be followed is worse than none.
const NO_USABLE_POSE = 'This design needs an upright cut-out photo, and none of the photos in this session fit it.';

export default function Builder({
  isEditingSingleSheet,
  backgrounds,
  favoriteIds,
  restrictToFavorites = false,
  imageTypes,
  initialSheets,
  option,
  packageId,
  packagePoses = [],
  poses,
  onConfirm,
  inReview = false,
}) {
  const { customer } = useCustomer();
  const { type } = useQueryString();
  // Data state
  const [additionalPoses, setAdditionalPoses] = useState(0);
  const previousAdditionalPoses = usePrevious(additionalPoses);
  const { data: printTemplates } = usePrintTemplate();
  const [unconfirmedPose, setUnconfirmedPose] = useState(null);
  const [foregrounds, setForegrounds] = useState([])
  const { data: pricelist } = usePricelist(packageId);
  // The student's senior season for THIS order, taken from the ordered poses'
  // folder (e.g. "seniors2027") -- the authoritative per-student class year. We
  // scope the overlay carousel to it so the class of 2026 and 2027 are offered
  // their own "Class of YYYY" set and old-year orders keep theirs. Falls back to
  // the package season, then any pose folder; undefined while poses load (then
  // the carousel shows all senior overlays until the season is known).
  const seniorSeason = useMemo(() => {
    const folders = (poses || []).map((p) => p.folder).filter(Boolean);
    return folders.find((f) => String(f).toLowerCase().startsWith('seniors'))
      || pricelist?.season
      || folders[0];
  }, [poses, pricelist?.season]);
  const { data: initialForegrounds } = useForegrounds(seniorSeason);
  const { data: studentForegrounds } = useStudentForegrounds(customer?.currentStudent?.id, pricelist?.season);

  // UI State
  const [sheets, setSheets] = useState([]);

  // const [isPoseModalOpen, setIsPoseModalOpen] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState('poses');
  const [sortedPoses, setSortedPoses] = useState([]);
  const {
    decrement,
    decrementDisabled,
    increment,
    incrementDisabled,
    index: currentSheetIndex,
    setIndex,
  } = useIndex({ maxIndex: sheets.length - 1 });

  const incrementHandler = () => {
    const subset = sheets
      .map((sheet) => ({ ...sheet }))
      .slice(0, currentSheetIndex + 1);

    const numUniquePoses = [...new Set([
      ...subset.filter((sheet) => sheet.imageType.name !== 'Yearbook Pose').map((sheet) => sheet.pose),
    ].map((pose) => pose.id))].length;

    setAdditionalPoses(Math.max(numUniquePoses - option.poses, 0));
    setSelectedIcon('poses')

    increment();
  };

  const decrementHandler = () => {
    const subset = sheets
      .map((sheet) => ({ ...sheet }))
      .slice(0, currentSheetIndex + 1);

    const numUniquePoses = [...new Set([
      ...subset.filter((sheet) => sheet.imageType.name !== 'Yearbook Pose').map((sheet) => sheet.pose),
    ].map((pose) => pose.id))].length;

    setAdditionalPoses(Math.max(numUniquePoses - option.poses, 0));

    decrement();
  };

  // Prefetch every offered pose's REAL dimensions into the shared cache, so a
  // product that refuses landscape can filter on measurement rather than on a
  // filename. Memoised: useImageOrientations keys its effect on the array, and
  // a fresh one each render would re-run it on every keystroke elsewhere.
  const posePreviewUrls = useMemo(
    () => sortedPoses.map((pose) => pose.watermarkUrl || pose.imageUrl).filter(Boolean),
    [sortedPoses],
  );
  const poseOrientations = useImageOrientations(posePreviewUrls);

  const { orientation: poseOrientation } = useImageOrientation(sheets[currentSheetIndex]?.pose?.watermarkUrl || sheets[currentSheetIndex]?.pose?.imageUrl);
  const { orientation: bgOrientation } = useImageOrientation(sheets[currentSheetIndex]?.background?.imageUrl);

  useDeepCompareEffect(() => {
    // The favorites PICKER restricts the builder to the chosen poses; every other caller
    // (senior yearbook-first sort, MultiPoseEdit's heart badge) only sorts favorites to
    // the front and keeps the full session. See visiblePoses.
    setSortedPoses(() => visiblePoses(poses, favoriteIds, option, restrictToFavorites));
  }, [poses, favoriteIds, option?.digitalImagesOnly, restrictToFavorites]);

  useEffect(() => {
    if (!poseOrientation) return;
    if (!bgOrientation) return;
    if (!sheets[currentSheetIndex]) return;
    if (poseOrientation === bgOrientation) return;

    const correctlyOrientatedBg = backgrounds.find((bg) => {
      const cachedData = IMAGE_ORIENTATION_CACHE[bg.imageUrl];

      return poseOrientation === cachedData?.orientation;
    });

    if (!correctlyOrientatedBg) return;

    sheets[currentSheetIndex].backgroundId = correctlyOrientatedBg.id;
    sheets[currentSheetIndex].background = correctlyOrientatedBg;

    setSheets((prev) => ([...prev]));
  }, [poseOrientation, bgOrientation, currentSheetIndex]);

  // Handlers
  const setPose = (newPose) => {
    if (sheets.length === 0) return;

    const currentSheet = sheets[currentSheetIndex];

    if (!currentSheet) return;

    // A sheet takes the pose AND the backdrop/overlay the pricelist assigns that pose -- the
    // favorites gallery drew it on that backdrop, so the sheet opens on it too. (The old code
    // computed these onto a copy it then never used, so every pose landed on whichever
    // backdrop the sheet already had.)
    const attrsFor = (sheet, pose) => poseSheetAttrs(pose, {
      packagePoses, backgrounds, pricelist, imageType: sheet.imageType,
    });

    // only look at the sheets the user has currently edited when factoring in additional poses
    const subset = sheets
      .map((sheet) => ({ ...sheet }))
      .slice(0, currentSheetIndex + 1);

    Object.assign(sheets[currentSheetIndex], attrsFor(currentSheet, newPose));

    // If a customer changes the default pose to a new pose, they can automatically be at the maximum number
    // of new sheets and be unaware of that. Change the pose for unseen sheets in that scenario
    const unseenSheets = sheets
      .slice(currentSheetIndex + 1);

    const subsetAdditionalPoses = calculateAdditionalPoses(subset, option);
    const totalAdditionalPoses = calculateAdditionalPoses(sheets, option);

    const currentlyUsedPoseIds = subset.map((sheet) => sheet.poseId);

    if (totalAdditionalPoses > subsetAdditionalPoses) {
      unseenSheets.filter((sheet) => sheet.imageType.name !== 'Yearbook Pose').forEach((sheet) => {
        if (currentlyUsedPoseIds.includes(sheet.poseId)) return;

        Object.assign(sheet, attrsFor(sheet, subset[currentSheetIndex].pose));
      });
    }
    // END

    Object.assign(subset[currentSheetIndex], attrsFor(currentSheet, newPose));

    setSheets(() => ([...subset, ...unseenSheets]));
    setAdditionalPoses(() => calculateAdditionalPoses(subset, option));
  };

  const setBackground = (newBackground) => {
    if (sheets.length === 0) return;

    const currentSheet = sheets[currentSheetIndex];

    if (!currentSheet) return;

    currentSheet.background = newBackground;
    currentSheet.backgroundId = newBackground.id;


    setSheets((prev) => ([...prev]));
  };

  const setForeground = (newForeground) => {
    if (sheets.length === 0) return;

    const currentSheet = sheets[currentSheetIndex];

    if (!currentSheet) return;

    if (newForeground.id === 99999) {
      currentSheet.foreground = undefined;
      currentSheet.foregroundId = undefined;
    } else {
      currentSheet.foreground = newForeground;
      currentSheet.foregroundId = newForeground.id;
    }

    setSheets((prev) => ([...prev]));
  };

  const onExtraPoseConfirm = () => setAdditionalPoses((prev) => prev + 1);

  // If a user confirms a pose, then add it and remove it from the unconfirmed pose
  useEffect(() => {
    if (additionalPoses === previousAdditionalPoses) return;
    if (!unconfirmedPose) return;

    setPose(unconfirmedPose);
    setUnconfirmedPose(null);
  }, [additionalPoses, previousAdditionalPoses, unconfirmedPose]);

  useEffect(() => {
    if (!initialForegrounds || !studentForegrounds) return;

    const clearForeground = { id: 99999 };

    if (studentForegrounds?.length > 0) {
      setForegrounds([clearForeground, ...studentForegrounds]);
    } else {
      setForegrounds([clearForeground, ...initialForegrounds]);
    }

  }, [initialForegrounds, studentForegrounds])

  // Initialize the sheets with placeholder data
  useEffect(() => {
    if (sortedPoses.length === 0) return;
    // imageTypes loads asynchronously; on first render a student's poses can
    // arrive before the image types do. Building sheets against an empty
    // imageTypes list dereferences an undefined image type (firstImageType.id),
    // which white-screened the senior builder for buy-all orders ("Cannot read
    // properties of undefined (reading 'id')"). Bail until they load —
    // imageTypes.length is in this effect's deps, so it re-runs and builds the
    // sheets once they arrive.
    if (imageTypes.length === 0) return;

    // ⚠️ `sortedPoses`, NOT the product-eligible set, and that asymmetry is a
    // trap rather than a live bug. Every add-on route into this component
    // supplies `initialSheets`, which overwrite this pose immediately, so no
    // reachable path builds an add-on sheet from here today. A future caller
    // that renders Builder with addon imageTypes and NO initial sheets would
    // open on a pose the carousel beside it refuses -- see the gifts screen's
    // re-validation effect for the shape of the fix if that ever happens.
    const firstPose = sortedPoses[0];

    let unindexedSheets;

    // For buy all / digital images only options, create one sheet per pose (not per image type)
    if (option.digitalImagesOnly && sortedPoses.length > 1) {
      console.log('BUY ALL: Creating sheets for', sortedPoses.length, 'poses');
      console.log('Digital Images Only:', option.digitalImagesOnly);
      console.log('Image Types:', imageTypes.length);

      // Get first non-yearbook image type for the buy all sheets (nullish-safe;
      // the imageTypes.length guard above guarantees this is defined here).
      const firstImageType = firstBuyAllImageType(imageTypes);

      // Get yearbook image type for the yearbook sheet
      const yearbookImageType = imageTypes.find(it => it.name === 'Yearbook Pose');

      // Create one sheet per pose
      const poseSheets = sortedPoses.map((pose) => ({
        imageType: firstImageType,
        imageTypeId: firstImageType.id,
        ...poseSheetAttrs(pose, {
          packagePoses, backgrounds, pricelist, imageType: firstImageType,
        }),
      }));

      // Add yearbook sheet if yearbook image type exists
      if (yearbookImageType) {
        poseSheets.push({
          imageType: yearbookImageType,
          imageTypeId: yearbookImageType.id,
          ...poseSheetAttrs(firstPose, {
            packagePoses, backgrounds, pricelist, imageType: yearbookImageType,
          }),
        });
      }

      unindexedSheets = poseSheets;
    } else {
      // Normal flow: create sheets based on image types
      unindexedSheets = imageTypes.map((imageType) => (
        new Array(imageType.count).fill(null).map(() => ({
          imageType,
          imageTypeId: imageType.id,
          ...poseSheetAttrs(firstPose, {
            packagePoses, backgrounds, pricelist, imageType,
          }),
        }))
      )).flat();
    }

    // Holiday items composite a transparent cutout, so a no-backdrop image type gets no
    // background -- poseSheetAttrs applies that from the imageType passed above.
    const nextSheets = unindexedSheets.map((sheet, i) => ({
      ...sheet,
      index: i,
    }));

    // If initial sheets are provided, then we will initialize the new sheets with the pose and
    // background information of the initial sheets. Since a user can select a new option, there may
    // be a mismatch in the imageType when selecting a new option or the number of sheets available.
    if (initialSheets.length > 0) {
      initialSheets.forEach((sheet, i) => {
        const index = nextSheets[i] ? i : 0;

        if (nextSheets[index]) {
          nextSheets[index].pose = sheet.pose;
          nextSheets[index].poseId = sheet.poseId.toString();
          nextSheets[index].background = sheet.background;
          nextSheets[index].backgroundId = sheet.backgroundId?.toString();
          nextSheets[index].foreground = sheet.foreground;
          nextSheets[index].foregroundId = sheet.foregroundId?.toString();
          nextSheets[index].imageType = sheet.imageType;
          nextSheets[index].imageTypeId = sheet.imageTypeId;
        }
      });
    }

    if (isEditingSingleSheet) {
      setSheets(() => [nextSheets[0]]);
    } else {
      console.log('BUY ALL: Setting sheets, total count:', nextSheets.length);
      setSheets(() => nextSheets);
    }
  }, [sortedPoses.length, initialSheets.length, imageTypes.length]);

  if (sheets.length === 0) return null;

  // ⚠️ THIS IS NOW A FALLBACK, NOT THE RULE. It infers "a JPG cannot work here"
  // from the Order template holding no element whose `src` mentions a
  // background -- and it answers `false` (JPGs fine) the moment an add-on has
  // NO Order template, which is how a JPG pose reached the weekly dry-erase
  // magnet. `acceptsJpg` consults the add-on's own column first and only falls
  // back to this, so every product that relied on it keeps behaving identically.
  const derivedJpgBlocked = () => {
    if (!sheets[currentSheetIndex]?.imageType?.component) return false;

    return !Object.values(sheets[currentSheetIndex].imageType.component.htmlElements).some((htmlElement) => {
      if (!htmlElement.data.src) return false;

      if (htmlElement.data.src.includes('background')) return true;
    });
  };

  // Present only for an ADD-ON sheet (addonSheetToSheet hangs it off imageType);
  // a package sheet -- an 8x10, wallets -- has no such rule and filters nothing.
  const currentAddon = sheets[currentSheetIndex]?.imageType?.addon;

  // What the POSE carousel may offer for the sheet being edited. One rule,
  // shared with the gifts product screen (poseEligibility.js), so a pose this
  // product refuses cannot be picked on one surface and carried into the other.
  const selectablePoses = eligiblePoses(
    sortedPoses, currentAddon, poseOrientations, derivedJpgBlocked(),
  );

  // ⚠️ THE SHEET'S OWN POSE IS JUDGED TOO, not only the carousel. A sheet that
  // already holds a refused pose -- added before the rule existed, or by an
  // all-JPG student before the shelf stopped offering the product -- would
  // otherwise be confirmed exactly as it stands, and print wrong. Filtering what
  // can be CHOSEN while confirming what is already chosen is the same half-fix
  // as filtering thumbnails without re-validating the selection.
  const sheetPose = sheets[currentSheetIndex]?.pose;
  const sheetPoseAllowed = !currentAddon || !sheetPose
    || eligiblePoses([sheetPose], currentAddon, poseOrientations, derivedJpgBlocked()).length > 0;

  // Nothing to choose, and a product that cares: say so, rather than an empty
  // strip the customer reads as "my photos failed to load".
  const nothingToChoose = !!currentAddon && selectablePoses.length === 0;

  const handleContinue = () => {
    if (selectedIcon === 'poses') {
      setSelectedIcon('backgrounds')
    }
    else if (selectedIcon === 'backgrounds') {
        setSelectedIcon('foregrounds')
    }
    else if (selectedIcon === 'foregrounds') {
      incrementHandler()
    }
  }

  const canSelectForegrounds = sheets[currentSheetIndex]
    && sheets[currentSheetIndex].pose.isPNG()
    && sheets[currentSheetIndex].imageType.name !== 'Yearbook Pose';

  // ⚠️ `derivedJpgBlocked`, NOT `filterJpgPoses`. These are two different
  // questions that happened to share an expression: "may a JPG be CHOSEN here"
  // (now also an admin decision) versus "does this TEMPLATE have a background
  // layer to pick at all". Routing this through the admin column meant setting
  // "cut-out PNG photos only" silently hid the background carousel and showed
  // "The background cannot change on the pose you have selected" -- on a PNG
  // cutout, which is precisely the pose that needs a background.
  const canSelectBackgrounds = canSelectForegrounds && !derivedJpgBlocked();

  const isAllJpg = poses.reduce((acc, cur) => acc && !cur.extension.toLowerCase().includes('png'), true);

  // The background carousel leads with the backdrop the current sheet's pose is specced
  // for -- the one the favorites screen showed it on -- then the rest in package order.
  // Re-derived from the POSE, not the sheet's current backgroundId, so it tracks what the
  // parent picks in the pose carousel and not what they later chose here.
  const currentPose = sheets[currentSheetIndex]?.pose;
  const orderedBackgrounds = backgroundsLedBy(
    backgrounds,
    currentPose && getAppropriateBg(packagePoses, currentPose, backgrounds, pricelist),
  );

  const renderLayout = () => (
    <>
      <div className={styles.carousels}>
        {nothingToChoose && (
          <p className={styles.jpgPoseSelected}>{NO_USABLE_POSE}</p>
        )}
        {!nothingToChoose && !sortedPoses.some(pose => pose.url.includes('PLACEHOLDER')) && (
          <Carousel
            items={selectablePoses}
            onClick={(pose) => setPose(pose)}
            text="POSE"
            favoriteIds={favoriteIds}
          />
        )}
        {!isAllJpg && (
          <div style={{ position: 'relative' }}>
            <div style={{ visibility: canSelectBackgrounds ? 'initial' : 'hidden' }}>
              <Carousel
                items={orderedBackgrounds}
                onClick={(background) => setBackground(background)}
                useOrientation={poseOrientation}
                text="BACKGROUND"
              />
            </div>
            {!canSelectBackgrounds && (
              <p className={styles.jpgPoseSelected}>The background cannot change on the pose you have selected.</p>
            )}
          </div>
        )}
        <div className={styles.btnContainers}>
          {!inReview && (
            <button disabled={decrementDisabled} onClick={decrementHandler} type="button">Back</button>
          )}
          {(!incrementDisabled || inReview) && (
            <button
              disabled={!sheetPoseAllowed}
              onClick={inReview ? () => onConfirm(sheets, 0) : incrementHandler}
              type="button"
            >
              Confirm
            </button>
          )}
          {incrementDisabled && packageId && (
            <button disabled={!sheetPoseAllowed} onClick={() => onConfirm(sheets, additionalPoses)} type="button">
              {sheets.length === 1 ? 'Add to Cart' : 'Add All to Cart'}
            </button>
          )}
        </div>
      </div>
    </>
  )

  const renderNewLayout = () => (
    <>
      <div className={styles.carousels}>
        {selectedIcon === 'poses' && nothingToChoose && (
          <p className={styles.jpgPoseSelected}>{NO_USABLE_POSE}</p>
        )}
        {selectedIcon === 'poses' && !nothingToChoose && (
          <Carousel
            items={selectablePoses}
            onClick={(pose) => setPose(pose)}
            favoriteIds={favoriteIds}
          />
        )}
        {!isAllJpg && selectedIcon === 'backgrounds' && (
          <div style={{ position: 'relative' }}>
            <div style={{ visibility: canSelectBackgrounds ? 'initial' : 'hidden' }}>
              <Carousel
                items={backgrounds}
                onClick={(background) => setBackground(background)}
                useOrientation={poseOrientation}
              />
            </div>
            {!canSelectBackgrounds && (
              <p className={styles.jpgPoseSelected}>The background cannot change on the pose you have selected.</p>
            )}
          </div>
        )}
        {!isAllJpg && selectedIcon === 'foregrounds' && (
          <div style={{ position: 'relative' }}>
            <div style={{ visibility: canSelectForegrounds ? 'initial' : 'hidden' }}>
              <Carousel
                items={foregrounds}
                onClick={(foreground) => setForeground(foreground)}
                useOrientation={poseOrientation}
                background={backgrounds.find(background => background.color.includes('Black'))}
              />
            </div>
            {!canSelectForegrounds && (
              <p className={styles.jpgPoseSelected}>The foreground cannot change on the pose you have selected.</p>
            )}
          </div>
        )}
        <NavBar selectedIcon={selectedIcon} setSelectedIcon={setSelectedIcon} />

        <div className={styles.btnContainers}>
          {!inReview && (
            <button disabled={decrementDisabled} onClick={decrementHandler} type="button">Back</button>
          )}
          {(!incrementDisabled || inReview) && (
            <button
              disabled={!sheetPoseAllowed}
              onClick={inReview ? () => onConfirm(sheets, 0) : handleContinue}
              type="button"
            >
              Confirm
            </button>
          )}
          {incrementDisabled && packageId && (
            <button disabled={!sheetPoseAllowed} onClick={() => onConfirm(sheets, additionalPoses)} type="button">
              {sheets.length === 1 ? 'Add to Cart' : 'Add All to Cart'}
            </button>
          )}
        </div>
      </div>
    </>
  )

  return (
    <>
      <div className={styles.builder}>
        {/*<ExtraPoseConfirmModal*/}
        {/*  additionalPoses={calculateAdditionalPoses(sheets, option)}*/}
        {/*  isOpen={isPoseModalOpen}*/}
        {/*  option={option}*/}
        {/*  setIsOpen={setIsPoseModalOpen}*/}
        {/*  onConfirm={onExtraPoseConfirm}*/}
        {/*/>*/}
        <PreviewArea
          setSheets={setSheets}
          currentSheetIndex={currentSheetIndex}
          sheet={sheets[currentSheetIndex]}
          imageTypes={imageTypes}
          sheets={sheets}
          type={type}
          printTemplates={printTemplates}
        />

        {pricelist?.isSeniorPortraits ? renderNewLayout() : renderLayout()}
      </div>
      <ProgressSheets sheets={sheets} index={currentSheetIndex} />
    </>
  );
}

Builder.defaultProps = {
  favoriteIds: [],
  restrictToFavorites: false,
  initialSheets: [],
  nextRoute: routes.review(),
};

Builder.propTypes = {
  favoriteIds: pt.arrayOf(pt.string),
  restrictToFavorites: pt.bool,
  initialSheets: pt.array,
  nextRoute: pt.string,
  option: pt.object.isRequired,
};
